import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Lock, Search, ArrowLeft, ArrowRight, SlidersHorizontal, Sparkles, Star, Layers, ChevronRight, BookOpen } from "lucide-react";
import axios from "axios";
import BookmarkButton from "@/components/BookmarkButton";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const TOOL_COLORS = {
  "ChatGPT":           "bg-emerald-100 text-emerald-700",
  "Claude":            "bg-orange-100 text-orange-700",
  "Gemini":            "bg-blue-100 text-blue-700",
  "Microsoft Copilot": "bg-sky-100 text-sky-700",
  "Perplexity":        "bg-teal-100 text-teal-700",
};

const TOOLS = ["ChatGPT", "Claude", "Gemini", "Microsoft Copilot", "Perplexity"];

const FUNCTION_COLORS = {
  blue: { bg: "bg-blue-600", light: "bg-blue-50 border-blue-100", text: "text-blue-700", subBg: "bg-blue-600 hover:bg-blue-700" },
  green: { bg: "bg-emerald-600", light: "bg-emerald-50 border-emerald-100", text: "text-emerald-700", subBg: "bg-emerald-600 hover:bg-emerald-700" },
  purple: { bg: "bg-purple-600", light: "bg-purple-50 border-purple-100", text: "text-purple-700", subBg: "bg-purple-600 hover:bg-purple-700" },
  orange: { bg: "bg-orange-600", light: "bg-orange-50 border-orange-100", text: "text-orange-700", subBg: "bg-orange-600 hover:bg-orange-700" },
  red: { bg: "bg-red-600", light: "bg-red-50 border-red-100", text: "text-red-700", subBg: "bg-red-600 hover:bg-red-700" },
  teal: { bg: "bg-teal-600", light: "bg-teal-50 border-teal-100", text: "text-teal-700", subBg: "bg-teal-600 hover:bg-teal-700" },
  indigo: { bg: "bg-indigo-600", light: "bg-indigo-50 border-indigo-100", text: "text-indigo-700", subBg: "bg-indigo-600 hover:bg-indigo-700" },
};

// Subcategory card for the drill-down view
const SubcategoryCard = ({ subcat, appCount, fnColor, onClick }) => {
  const colors = FUNCTION_COLORS[fnColor] || FUNCTION_COLORS.blue;
  return (
    <div
      className="relative bg-white border border-slate-200 rounded-xl p-5 cursor-pointer hover:border-blue-300 hover:shadow-md transition-all group"
      onClick={onClick}
      data-testid={`subcategory-card-${subcat.subcategory_id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${colors.light} border`}>
          <Layers size={16} className={colors.text} />
        </div>
        <Badge className={`text-xs border-0 ${colors.light} ${colors.text}`}>
          {appCount} app{appCount !== 1 ? "s" : ""}
        </Badge>
      </div>
      <h3 className="font-semibold text-slate-900 text-sm mb-1 leading-snug">{subcat.name}</h3>
      <p className="text-xs text-slate-500 leading-relaxed line-clamp-2 mb-4">{subcat.description}</p>
      <div className="flex items-center justify-between">
        <span className={`text-xs font-medium ${appCount > 0 ? colors.text : "text-slate-400"}`}>
          {appCount > 0 ? "Browse applications" : "Coming soon"}
        </span>
        <ChevronRight size={14} className={`transition-transform group-hover:translate-x-0.5 ${appCount > 0 ? "text-slate-400" : "text-slate-200"}`} />
      </div>
    </div>
  );
};

// App card component
const AppCard = ({ app, onClick, isFavourite = false, onToggleFav, isLocked = false }) => {
  const isPremium = app.is_premium;
  const tools = app.recommended_tools || [];
  const variations = app.prompt_variations ? Object.keys(app.prompt_variations) : [];

  return (
    <div
      className={`relative bg-white border rounded-xl p-5 hover-lift card-shadow cursor-pointer fade-in transition-all ${isLocked ? "border-slate-200 opacity-80 hover:opacity-100" : "border-slate-200"}`}
      onClick={onClick}
      data-testid={`app-card-${app.app_id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 mr-2">
          <h3 className="font-semibold text-slate-900 text-sm leading-snug flex items-center gap-1.5">
            {isLocked && isPremium && <Lock size={12} className="text-slate-400 flex-shrink-0" />}
            {app.title}
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">{app.category}</p>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <Badge className={`text-xs border-0 ${isPremium ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
            {isPremium ? `${app.credit_cost} credits` : "FREE"}
          </Badge>
          {onToggleFav && !isLocked && (
            <BookmarkButton appId={app.app_id} initialState={isFavourite} onToggle={onToggleFav} size={14} />
          )}
        </div>
      </div>
      <p className="text-xs text-slate-600 leading-relaxed line-clamp-3 mb-4">{app.business_context}</p>
      {app.avg_rating > 0 && (
        <div className="flex items-center gap-1.5 mb-3">
          {[1,2,3,4,5].map(i => (
            <Star key={i} size={11} className={i <= Math.round(app.avg_rating) ? "text-amber-400 fill-amber-400" : "text-slate-200 fill-slate-200"} />
          ))}
          <span className="text-xs font-semibold text-slate-600">{app.avg_rating}</span>
          <span className="text-xs text-slate-400">({app.review_count})</span>
        </div>
      )}
      {isLocked && isPremium ? (
        <div className="flex items-center justify-between pt-3 border-t border-slate-100">
          <span className="text-xs text-amber-600 font-medium flex items-center gap-1"><Lock size={11} /> Premium — upgrade required</span>
          <ArrowRight size={14} className="text-blue-400" />
        </div>
      ) : (
        <div className="flex items-center justify-between">
          <div className="flex gap-1 flex-wrap items-center">
            {tools.slice(0, 2).map(t => (
              <span key={t} className={`text-xs px-2 py-0.5 rounded-full font-medium ${TOOL_COLORS[t] || "bg-slate-100 text-slate-500"}`}>{t}</span>
            ))}
            {tools.length > 2 && <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">+{tools.length - 2}</span>}
            {variations.length > 0 && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600 border border-indigo-100 font-medium flex items-center gap-1" data-testid={`variations-badge-${app.app_id}`}>
                <Sparkles size={9} /> {variations.length} tool {variations.length === 1 ? "version" : "versions"}
              </span>
            )}
          </div>
          <ArrowRight size={14} className="text-slate-400 flex-shrink-0" />
        </div>
      )}
    </div>
  );
};

const FunctionPage = () => {
  const { functionId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();

  const [fnInfo, setFnInfo] = useState(null);
  const [subcategories, setSubcategories] = useState([]);
  const [selectedSubcat, setSelectedSubcat] = useState(searchParams.get("subcategory") || null);
  const [apps, setApps] = useState([]);
  const [visibleCount, setVisibleCount] = useState(10);
  const [loading, setLoading] = useState(true);
  const [appsLoading, setAppsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedTool, setSelectedTool] = useState(searchParams.get("tool") || "");
  const [sort, setSort] = useState("free_first");
  const [favouriteIds, setFavouriteIds] = useState([]);

  // Load function info + subcategories
  useEffect(() => {
    Promise.all([
      axios.get(`${API}/functions`, { withCredentials: true }),
      axios.get(`${API}/subcategories`, { params: { function_id: functionId } }),
    ]).then(([fnsRes, subcatRes]) => {
      const fn = fnsRes.data.find(f => f.function_id === functionId);
      setFnInfo(fn);
      setSubcategories(subcatRes.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [functionId]);

  // Load apps when subcategory is selected
  useEffect(() => {
    if (!selectedSubcat) { setApps([]); return; }
    setAppsLoading(true);
    axios.get(`${API}/apps`, { params: { function_id: functionId, subcategory_id: selectedSubcat }, withCredentials: true })
      .then(res => setApps(res.data))
      .catch(() => {})
      .finally(() => setAppsLoading(false));
  }, [selectedSubcat, functionId]);

  useEffect(() => {
    if (user) {
      axios.get(`${API}/favourites/ids`, { withCredentials: true })
        .then(res => setFavouriteIds(res.data)).catch(() => {});
    }
  }, [user]);

  const handleSelectSubcat = (subcatId) => {
    setSelectedSubcat(subcatId);
    setSearch("");
    setSelectedTool("");
    setVisibleCount(10);
    setSearchParams(subcatId ? { subcategory: subcatId } : {});
  };

  const handleToggleFav = (appId, state) => {
    setFavouriteIds(prev => state ? [...prev, appId] : prev.filter(id => id !== appId));
  };

  const hasPremiumAccess = () => {
    if (!user) return false;
    if (user.subscription_plan === "full") return true;
    const accessible = user.accessible_functions || [];
    if (accessible === "all") return true;
    return accessible.includes(functionId);
  };
  const canAccessPremium = hasPremiumAccess();

  const currentSubcat = subcategories.find(s => s.subcategory_id === selectedSubcat);
  const fnColor = fnInfo?.color || "blue";
  const colors = FUNCTION_COLORS[fnColor] || FUNCTION_COLORS.blue;

  const filteredApps = apps.filter(app => {
    const matchSearch = !search ||
      app.title.toLowerCase().includes(search.toLowerCase()) ||
      (app.recommended_tools || []).some(t => t.toLowerCase().includes(search.toLowerCase()));
    const matchTool = !selectedTool || (app.recommended_tools || []).includes(selectedTool);
    return matchSearch && matchTool;
  });

  const sortedApps = [...filteredApps].sort((a, b) => {
    if (sort === "free_first") return a.is_premium - b.is_premium;
    if (sort === "premium_first") return b.is_premium - a.is_premium;
    return 0;
  });

  const usedTools = [...new Set(apps.flatMap(a => a.recommended_tools || []))].filter(t => TOOLS.includes(t));

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-slate-500 mb-6">
          <button onClick={() => navigate("/dashboard")} className="hover:text-slate-900 transition-colors flex items-center gap-1.5">
            <ArrowLeft size={14} /> Dashboard
          </button>
          <ChevronRight size={13} className="text-slate-300" />
          {selectedSubcat ? (
            <>
              <button
                onClick={() => handleSelectSubcat(null)}
                className="hover:text-slate-900 transition-colors capitalize font-medium"
                data-testid="back-to-subcategories-btn"
              >
                {fnInfo?.name || functionId}
              </button>
              <ChevronRight size={13} className="text-slate-300" />
              <span className="text-slate-900 font-medium">{currentSubcat?.name}</span>
            </>
          ) : (
            <span className="text-slate-900 font-medium capitalize">{fnInfo?.name || functionId}</span>
          )}
        </div>

        {/* Function Header */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-2xl font-bold text-slate-900">{fnInfo?.name || functionId}</h1>
                {selectedSubcat && currentSubcat && (
                  <>
                    <ChevronRight size={18} className="text-slate-300" />
                    <h2 className={`text-lg font-semibold ${colors.text}`}>{currentSubcat.name}</h2>
                  </>
                )}
              </div>
              <p className="text-slate-500 text-sm">
                {selectedSubcat && currentSubcat ? currentSubcat.description : fnInfo?.description}
              </p>
              {!selectedSubcat && (
                <div className="flex items-center gap-3 mt-3">
                  <span className="text-sm text-slate-500">
                    <span className="font-semibold text-slate-900">{subcategories.length}</span> subcategories
                  </span>
                  <span className="text-slate-300">·</span>
                  <span className="text-sm text-slate-500">
                    <span className="font-semibold text-slate-900">{fnInfo?.app_count || 0}</span> applications
                  </span>
                </div>
              )}
              {selectedSubcat && (
                <div className="flex items-center gap-3 mt-3">
                  <span className="text-sm text-slate-500">
                    <span className="font-semibold text-emerald-600">{apps.filter(a => !a.is_premium).length}</span> free
                  </span>
                  <span className="text-sm text-slate-500">
                    <span className="font-semibold text-amber-600">{apps.filter(a => a.is_premium).length}</span> premium
                  </span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              {selectedSubcat && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleSelectSubcat(null)}
                  data-testid="back-to-subcategories-header-btn"
                >
                  <Layers size={14} className="mr-1.5" /> All Subcategories
                </Button>
              )}
              {!canAccessPremium && user?.subscription_plan === "free" && (
                <Button
                  onClick={() => navigate("/pricing")}
                  className="bg-blue-600 hover:bg-blue-700 text-white flex-shrink-0"
                  size="sm"
                >
                  Unlock All Applications
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* SUBCATEGORY GRID VIEW */}
        {!selectedSubcat && (
          <>
            {loading ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {Array(8).fill(0).map((_, i) => (
                  <div key={i} className="bg-white rounded-xl p-5 border">
                    <Skeleton className="w-9 h-9 rounded-lg mb-3" />
                    <Skeleton className="h-4 w-3/4 mb-2" />
                    <Skeleton className="h-3 w-full mb-1" />
                    <Skeleton className="h-3 w-2/3" />
                  </div>
                ))}
              </div>
            ) : subcategories.length === 0 ? (
              <div className="text-center py-16 text-slate-400">
                <BookOpen size={32} className="mx-auto mb-3 opacity-40" />
                <p className="font-medium">No subcategories yet</p>
                <p className="text-sm mt-1">Check back soon as content is added to this function.</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="subcategory-grid">
                {subcategories.map(subcat => (
                  <SubcategoryCard
                    key={subcat.subcategory_id}
                    subcat={subcat}
                    appCount={subcat.app_count || 0}
                    fnColor={fnColor}
                    onClick={() => subcat.app_count > 0 && handleSelectSubcat(subcat.subcategory_id)}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {/* APPS VIEW (after subcategory selected) */}
        {selectedSubcat && (
          <>
            {/* Tool filter */}
            {usedTools.length > 0 && (
              <div className="mb-4">
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setSelectedTool("")}
                    className={`px-3 py-1.5 text-sm rounded-lg border font-medium transition-all ${!selectedTool ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}
                  >
                    All Tools
                  </button>
                  {usedTools.map(tool => (
                    <button
                      key={tool}
                      onClick={() => setSelectedTool(selectedTool === tool ? "" : tool)}
                      className={`px-3 py-1.5 text-sm rounded-lg border font-medium transition-all ${selectedTool === tool ? `${TOOL_COLORS[tool]} border-transparent` : "bg-white text-slate-600 border-slate-200 hover:border-blue-300"}`}
                      data-testid={`tool-filter-${tool.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      {tool}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Search + Sort */}
            <div className="bg-white border border-slate-200 rounded-xl p-4 mb-6">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Search applications..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="pl-9"
                    data-testid="app-search-input"
                  />
                </div>
                <Select value={sort} onValueChange={setSort}>
                  <SelectTrigger className="sm:w-40" data-testid="sort-select">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="newest">Default Order</SelectItem>
                    <SelectItem value="free_first">Free First</SelectItem>
                    <SelectItem value="premium_first">Premium First</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {appsLoading ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {Array(6).fill(0).map((_, i) => (
                  <div key={i} className="bg-white rounded-xl p-5 border">
                    <Skeleton className="h-5 w-3/4 mb-2" />
                    <Skeleton className="h-4 w-1/2 mb-3" />
                    <Skeleton className="h-4 w-full mb-1" />
                    <Skeleton className="h-4 w-full mb-4" />
                    <Skeleton className="h-6 w-32" />
                  </div>
                ))}
              </div>
            ) : sortedApps.length === 0 ? (
              <div className="text-center py-16 text-slate-400">
                <SlidersHorizontal size={28} className="mx-auto mb-3 opacity-40" />
                <p className="text-lg font-medium mb-2">
                  {apps.length === 0 ? "No applications in this subcategory yet" : "No applications match your filters"}
                </p>
                <p className="text-sm mb-4">
                  {apps.length === 0 ? "Applications will be added here soon." : "Try a different tool or clear your search."}
                </p>
                {apps.length > 0 && (
                  <Button variant="outline" size="sm" onClick={() => { setSelectedTool(""); setSearch(""); }}>
                    Clear Filters
                  </Button>
                )}
              </div>
            ) : (
              <>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="apps-grid">
                  {sortedApps.slice(0, visibleCount).map(app => (
                    <AppCard
                      key={app.app_id}
                      app={app}
                      isFavourite={favouriteIds.includes(app.app_id)}
                      onToggleFav={user ? handleToggleFav : null}
                      isLocked={app.is_premium && !canAccessPremium}
                      onClick={() => navigate(`/app/${app.app_id}`)}
                    />
                  ))}
                </div>
                {/* Pagination */}
                {sortedApps.length > visibleCount && (
                  <div className="flex flex-col items-center gap-2 mt-6">
                    <p className="text-sm text-slate-500">
                      Showing <span className="font-semibold text-slate-900">{Math.min(visibleCount, sortedApps.length)}</span> of <span className="font-semibold text-slate-900">{sortedApps.length}</span> applications
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => setVisibleCount(c => c + 10)}
                      data-testid="load-more-btn"
                      className="border-slate-200 hover:border-blue-300"
                    >
                      Load more applications
                    </Button>
                  </div>
                )}
                {sortedApps.length <= visibleCount && sortedApps.length > 0 && (
                  <p className="text-center text-xs text-slate-400 mt-4">
                    All {sortedApps.length} application{sortedApps.length !== 1 ? "s" : ""} shown
                  </p>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default FunctionPage;
